/**
 * W-22576228: Cache for resolving datasource repositoryUrl/id → display name.
 * Populated by ServerModelHelper.dataSources() when datasources are fetched.
 * Consumed by ConnectionHoverPanel popover and connections pane to fix composed DS connection names.
 */

/** Connection type for published datasource references (composed DS parents) */
export const TABLEAUSERVER_CONNECTION_TYPE = 'tableauserver';

/** Filter field name for datasource repositoryUrl lookups */
export const REPOSITORY_URL_FIELD = 'repositoryUrl';

const cache: Record<string, string> = {};
const pending: Set<string> = new Set();

/** Minimal shape for items that have connectionDetails (IDataSourceInfo, IDataConnectionInfo) */
interface HasConnectionDetails {
  connectionDetails?: { type: string, id?: string, name?: string };
  name?: string;
}

/** Minimal shape for datasource items in API responses */
interface DsEntry {
  id: string;
  name?: string;
  repositoryUrl?: string;
}

export const DatasourceNameCache = {
  /**
   * Populate cache from a datasource array. Stores both repositoryUrl→name and id→name
   * so resolution works regardless of which key a connection provides.
   */
  populate(datasources: DsEntry[]): void {
    for (const ds of datasources) {
      if (ds.name) {
        if (ds.repositoryUrl) {
          cache[ds.repositoryUrl] = ds.name;
          pending.delete(ds.repositoryUrl);
        }
        cache[ds.id] = ds.name;
        pending.delete(ds.id);
      }
    }
  },

  /** Look up display name by repositoryUrl or id */
  resolve(key: string): string | undefined {
    return cache[key];
  },

  /**
   * Patch connectionDetails.name on all tableauserver connections in an array.
   * Tries resolving by repoUrl (connectionDetails.name) then by id.
   * Skips items where resolved name equals item's own name (avoids self-match for datasources).
   * Unresolved repoUrls are tracked for later async resolution.
   */
  resolveTableauServerConnections<T extends HasConnectionDetails>(items: T[]): void {
    for (const item of items) {
      if (item.connectionDetails?.type === TABLEAUSERVER_CONNECTION_TYPE && item.connectionDetails?.name) {
        const repoUrl = item.connectionDetails.name;
        const id = item.connectionDetails.id;
        const resolvedName = cache[repoUrl] || (id && cache[id]) || undefined;
        if (resolvedName && resolvedName !== item.name) {
          item.connectionDetails = { ...item.connectionDetails, name: resolvedName };
        } else if (!resolvedName) {
          pending.add(repoUrl);
        }
      }
    }
  },

  /** Whether there are unresolved repoUrls from a prior resolveTableauServerConnections call */
  hasPending(): boolean {
    return pending.size > 0;
  },

  /** Get the set of unresolved repoUrls and clear them (one attempt per repoUrl) */
  consumePending(): string[] {
    const result = Array.from(pending);
    pending.clear();
    return result;
  },
};
